import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import moment from "moment";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { getMemberTrackersApi } from "src/DAL/tracker/tracker";
import {
  clearLegacyJourneyDemoStorage,
  groupMemberTrackers,
  PREVIEW_LIMIT,
  PRIMARY,
  TRACKER_LIST_SECTIONS,
} from "./memberTrackersStorage";
import { JourneyLockChip } from "./JourneyPasswordGate";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const SECTION_META = {
  [TRACKER_LIST_SECTIONS.MY]: {
    title: "My journeys",
    blurb: "Journeys you’re working on or have already completed.",
  },
  [TRACKER_LIST_SECTIONS.NEW]: {
    title: "New to you",
    blurb: "Journeys you can join — not started yet.",
  },
  [TRACKER_LIST_SECTIONS.LEFT]: {
    title: "Past journeys",
    blurb: "Journeys you've left. You can view your history or rejoin.",
  },
};

const VALID_SECTIONS = Object.keys(SECTION_META);

const TrackerCard = ({ tracker, meta, onOpen, variant = "my" }) => (
  <Box
    component="button"
    type="button"
    onClick={onOpen}
    sx={{
      textAlign: "left",
      border: `1px solid ${BORDER}`,
      borderRadius: "14px",
      bgcolor: "#fff",
      p: 2.25,
      cursor: "pointer",
      transition: "border-color 0.15s, box-shadow 0.15s",
      "&:hover": {
        borderColor: PRIMARY,
        boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        mb: 1.25,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "11px",
          bgcolor:
            "color-mix(in srgb, var(--portal-theme-primary) 12%, white)",
          color: PRIMARY,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {meta.isJourney ? (
          <RouteOutlinedIcon fontSize="small" />
        ) : (
          <FlagOutlinedIcon fontSize="small" />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: 0.75,
        }}
      >
        {meta.isPasswordLocked ? <JourneyLockChip /> : null}
        <Chip
          size="small"
          label={
            variant === "left"
              ? tracker.statusName || meta.statusLabel || "Left"
              : tracker.statusName || (variant === "new" ? "New" : "Active")
          }
          sx={{
            height: 24,
            fontWeight: 700,
            fontSize: 11.5,
            bgcolor: `${tracker.statusColor || PRIMARY}18`,
            color: tracker.statusColor || PRIMARY,
          }}
        />
      </Box>
    </Box>

    <Typography
      sx={{
        fontWeight: 800,
        fontSize: 16.5,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.3,
        mb: 0.5,
      }}
    >
      {tracker.name}
    </Typography>
    <Typography
      sx={{
        fontSize: 13.5,
        color: MUTED,
        lineHeight: 1.5,
        mb: 1.75,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {tracker.description}
    </Typography>

    {variant !== "new" ? (
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.75,
          }}
        >
          <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>
            Progress
          </Typography>
          <Typography sx={{ fontSize: 12, color: INK, fontWeight: 700 }}>
            {meta.progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={meta.progress}
          sx={{
            height: 7,
            borderRadius: 99,
            bgcolor: "#eef2f6",
            "& .MuiLinearProgress-bar": {
              borderRadius: 99,
              bgcolor: PRIMARY,
            },
          }}
        />
      </Box>
    ) : null}

    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        pt: 0.5,
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
        Coach:{" "}
        <Box component="span" sx={{ color: INK, fontWeight: 650 }}>
          {tracker.coachName || "—"}
        </Box>
      </Typography>
      {variant === "my" ? (
        <>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>
            Submissions:{" "}
            <Box component="span" sx={{ color: INK, fontWeight: 650 }}>
              {meta.submissionsCount}
            </Box>
          </Typography>
          {meta.currentStep ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Next:{" "}
              <Box component="span" sx={{ color: PRIMARY, fontWeight: 700 }}>
                {meta.currentStep.name}
              </Box>
            </Typography>
          ) : null}
          {meta.lastSubmittedAt ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Last: {moment(meta.lastSubmittedAt).fromNow()}
            </Typography>
          ) : null}
        </>
      ) : variant === "left" ? (
        <>
          {meta.leftAt ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Left: {moment(meta.leftAt).format("D MMM YYYY")}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: 12.5, color: PRIMARY, fontWeight: 700 }}>
            View history or rejoin
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: PRIMARY, fontWeight: 700 }}>
          Tap to open & start
        </Typography>
      )}
    </Box>
  </Box>
);

const TrackerSection = ({
  sectionKey,
  rows,
  preview = true,
  search,
  onViewAll,
  onOpen,
}) => {
  const meta = SECTION_META[sectionKey];
  const needle = search.trim().toLowerCase();
  const filtered = rows.filter(({ tracker }) => {
    if (!needle) return true;
    return `${tracker.name} ${tracker.description} ${tracker.coachName}`
      .toLowerCase()
      .includes(needle);
  });
  const visible =
    preview && !needle ? filtered.slice(0, PREVIEW_LIMIT) : filtered;
  const remaining = filtered.length - visible.length;
  const variant =
    sectionKey === TRACKER_LIST_SECTIONS.NEW
      ? "new"
      : sectionKey === TRACKER_LIST_SECTIONS.LEFT
        ? "left"
        : "my";

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.75,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 18,
              color: INK,
              letterSpacing: "-0.02em",
            }}
          >
            {meta.title}
            <Box
              component="span"
              sx={{ ml: 1, fontWeight: 650, fontSize: 14, color: MUTED }}
            >
              ({filtered.length})
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 0.35 }}>
            {meta.blurb}
          </Typography>
        </Box>
        {preview && remaining > 0 ? (
          <Box
            component="button"
            type="button"
            onClick={onViewAll}
            sx={{
              border: "none",
              background: "none",
              cursor: "pointer",
              color: PRIMARY,
              fontWeight: 800,
              fontSize: 13.5,
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all ({filtered.length})
          </Box>
        ) : null}
      </Box>

      {filtered.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            border: `1px dashed ${BORDER}`,
            borderRadius: "14px",
            bgcolor: "#f8fafc",
          }}
        >
          <Typography sx={{ color: MUTED, fontSize: 14 }}>
            {needle
              ? "No matches in this section."
              : sectionKey === TRACKER_LIST_SECTIONS.MY
                ? "You’re not on any journeys yet."
                : sectionKey === TRACKER_LIST_SECTIONS.LEFT
                  ? "You haven’t left any journeys."
                  : "Nothing new right now — check back soon."}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
              xl: "1fr 1fr 1fr",
            },
            gap: 2,
          }}
        >
          {visible.map(({ tracker, meta: cardMeta }) => (
            <TrackerCard
              key={tracker.id}
              tracker={tracker}
              meta={cardMeta}
              variant={variant}
              onOpen={() => onOpen(tracker.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function MemberTrackersList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useContentSetting();
  const [search, setSearch] = useState("");
  const [trackers, setTrackers] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [loadMessage, setLoadMessage] = useState("");

  const sectionParam = String(searchParams.get("section") || "")
    .trim()
    .toLowerCase();
  const activeSection = VALID_SECTIONS.includes(sectionParam)
    ? sectionParam
    : null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadState("loading");
      try {
        const res = await getMemberTrackersApi();
        if (cancelled) return;
        if (res?.code === 200 && Array.isArray(res.trackers)) {
          setTrackers(res.trackers);
          setLoadState("ready");
          setLoadMessage("");
        } else {
          setTrackers([]);
          setLoadState("error");
          setLoadMessage(res?.message || "Could not load journeys");
        }
      } catch (_err) {
        if (cancelled) return;
        setTrackers([]);
        setLoadState("error");
        setLoadMessage("Could not load journeys");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userInfo?._id]);

  useEffect(() => {
    clearLegacyJourneyDemoStorage();
  }, []);

  const grouped = useMemo(() => groupMemberTrackers(trackers), [trackers]);

  const openTracker = (id) => navigate(`/trackers/${id}`);
  const openSection = (key) => setSearchParams({ section: key });
  const clearSection = () => setSearchParams({});

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-lg-8 col-sm-12">
          {activeSection ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.25,
                cursor: "pointer",
                width: "fit-content",
                color: MUTED,
                "&:hover": { color: PRIMARY },
              }}
              onClick={clearSection}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 650 }}>
                All journeys
              </Typography>
            </Box>
          ) : null}
          <h2 style={{ marginBottom: 6 }}>
            {activeSection
              ? SECTION_META[activeSection].title
              : "Journeys"}
          </h2>
          <Typography sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.5 }}>
            {activeSection
              ? SECTION_META[activeSection].blurb
              : "Journeys you’re on, plus new ones you can join."}
          </Typography>
        </div>
      </div>

      <Box sx={{ mb: 3, maxWidth: 420 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search journeys…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: MUTED, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#fff",
            },
          }}
        />
      </Box>

      {loadState === "loading" ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : loadState === "error" ? (
        <Typography sx={{ color: MUTED, fontSize: 14.5 }}>
          {loadMessage}
        </Typography>
      ) : (
        <>
          {activeSection === TRACKER_LIST_SECTIONS.MY ? (
            <TrackerSection
              sectionKey={TRACKER_LIST_SECTIONS.MY}
              rows={grouped.my}
              preview={false}
              search={search}
              onOpen={openTracker}
            />
          ) : activeSection === TRACKER_LIST_SECTIONS.NEW ? (
            <TrackerSection
              sectionKey={TRACKER_LIST_SECTIONS.NEW}
              rows={grouped.new}
              preview={false}
              search={search}
              onOpen={openTracker}
            />
          ) : activeSection === TRACKER_LIST_SECTIONS.LEFT ? (
            <TrackerSection
              sectionKey={TRACKER_LIST_SECTIONS.LEFT}
              rows={grouped.left}
              preview={false}
              search={search}
              onOpen={openTracker}
            />
          ) : (
            <>
              <TrackerSection
                sectionKey={TRACKER_LIST_SECTIONS.MY}
                rows={grouped.my}
                preview
                search={search}
                onViewAll={() => openSection(TRACKER_LIST_SECTIONS.MY)}
                onOpen={openTracker}
              />
              <TrackerSection
                sectionKey={TRACKER_LIST_SECTIONS.NEW}
                rows={grouped.new}
                preview
                search={search}
                onViewAll={() => openSection(TRACKER_LIST_SECTIONS.NEW)}
                onOpen={openTracker}
              />
              {grouped.left.length > 0 ? (
                <TrackerSection
                  sectionKey={TRACKER_LIST_SECTIONS.LEFT}
                  rows={grouped.left}
                  preview
                  search={search}
                  onViewAll={() => openSection(TRACKER_LIST_SECTIONS.LEFT)}
                  onOpen={openTracker}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
